import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { AgentCardLoading } from '@atlassian/agent-profile-card/loading';

import { useFireRenderedSpinner } from '../../hooks/useProfileCardAnalytics';
import type { PersonProfileData } from '../../types';
import { CardPopupWrapper } from './CardPopupWrapper';

const messages = defineMessages({
	managerHeading: {
		id: 'user-profile-card.CardLoading.managerHeading',
		defaultMessage: 'Manager',
		description: 'Section title for the manager row on the user profile card',
	},
});

const styles = cssMap({
	inner: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	fill: {
		flex: '1',
		minWidth: '0px',
	},
	linkRow: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	managerRow: {
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

function HeaderLoading({
	profileData,
	isReduced = false,
}: {
	profileData?: PersonProfileData;
	isReduced?: boolean;
}) {
	const subtitle = [profileData?.jobTitle, profileData?.department].filter(Boolean).join(' • ');

	return (
		<Stack space="space.075">
			<Inline spread="space-between" alignBlock="start">
				{profileData?.avatarUrl || profileData?.name ? (
					<Avatar src={profileData.avatarUrl ?? undefined} name={profileData.name} size="xlarge" />
				) : (
					<Skeleton width="96px" height="96px" borderRadius={token('radius.full')} />
				)}
				{!isReduced && (
					<Inline space="space.100" alignBlock="center">
						<Skeleton width="32px" height="32px" borderRadius={token('radius.medium')} />
						<Skeleton width="96px" height="32px" borderRadius={token('radius.medium')} />
						<Skeleton width="32px" height="32px" borderRadius={token('radius.medium')} />
					</Inline>
				)}
			</Inline>
			<Stack space="space.050">
				{profileData?.name ? (
					<Heading as="h2" size="medium">
						{profileData.name}
					</Heading>
				) : (
					<Skeleton width="45%" height="24px" borderRadius={token('radius.small')} />
				)}
				{subtitle ? (
					<Text color="color.text.subtlest">{subtitle}</Text>
				) : (
					<Skeleton width="80%" height="20px" borderRadius={token('radius.small')} />
				)}
				{profileData?.location ? (
					<Text color="color.text.subtlest">{profileData.location}</Text>
				) : (
					<Skeleton width="50%" height="20px" borderRadius={token('radius.small')} />
				)}
				{profileData?.email ? (
					<Text color="color.text.subtlest">{profileData.email}</Text>
				) : (
					<Skeleton width="80%" height="20px" borderRadius={token('radius.small')} />
				)}
			</Stack>
		</Stack>
	);
}

function ManagerSectionLoading({ profileData }: { profileData?: PersonProfileData }) {
	const intl = useIntl();
	const manager = profileData?.manager;

	return (
		<Stack space="space.075">
			{manager?.name ? (
				<Text as="p" weight="medium">
					{intl.formatMessage(messages.managerHeading)}
				</Text>
			) : (
				<Skeleton width="60px" height="24px" borderRadius={token('radius.small')} />
			)}
			<Inline space="space.100" alignBlock="center" xcss={styles.managerRow}>
				{manager?.avatarUrl || manager?.name ? (
					<Avatar src={manager.avatarUrl ?? undefined} name={manager.name ?? undefined} />
				) : (
					<Skeleton width="36px" height="36px" borderRadius={token('radius.full')} />
				)}
				<Stack space="space.050" xcss={styles.fill}>
					{manager?.name ? (
						<Text>{manager.name}</Text>
					) : (
						<Skeleton width="45%" height="14px" borderRadius={token('radius.small')} />
					)}
					{manager?.jobTitle ? (
						<Text color="color.text.subtlest" size="small">
							{manager.jobTitle}
						</Text>
					) : (
						<Skeleton width="65%" height="14px" borderRadius={token('radius.small')} />
					)}
				</Stack>
			</Inline>
		</Stack>
	);
}

function LinkRowSkeleton() {
	return (
		<Box xcss={styles.linkRow}>
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Inline space="space.100" alignBlock="center" xcss={styles.fill}>
					<Skeleton width="36px" height="36px" borderRadius={token('radius.large')} />
					<Stack xcss={styles.fill} space="space.050">
						<Skeleton width="40%" height="16px" borderRadius={token('radius.small')} />
						<Skeleton width="70%" height="16px" borderRadius={token('radius.small')} />
					</Stack>
				</Inline>
				<Skeleton width="32px" height="16px" borderRadius={token('radius.small')} />
			</Inline>
		</Box>
	);
}

function RecentWorkSectionSkeleton() {
	return (
		<Stack space="space.075">
			<Skeleton width="90px" height="24px" borderRadius={token('radius.small')} />
			<Stack space="space.050">
				<LinkRowSkeleton />
				<LinkRowSkeleton />
				<LinkRowSkeleton />
			</Stack>
		</Stack>
	);
}

/**
 * Loading state shown while the profile card entry chunk resolves. Mirrors the
 * populated card layout:
 *
 * - Header: 96px avatar + action buttons (icon + "View profile") above the
 *   name + pronouns, subtitle (up to 2 lines), and location.
 * - Manager: heading + 24px avatar with name and job title lines.
 * - Recent work: heading + 3 bordered link rows that mirror the compact
 *   `LinkList` rendering used in `RecentWorkSection`
 *
 * Keeping the skeleton shape close to the populated layout minimises the
 * perceived layout jump when data resolves. Fires `rendered.spinner` on mount
 * so the event only emits when the spinner is actually on screen.
 */
export function CardLoading({
	testId,
	profileData,
	isReduced = false,
	isAgent = false,
}: {
	testId?: string;
	profileData?: PersonProfileData;
	isReduced?: boolean;
	isAgent?: boolean;
}): React.JSX.Element {
	useFireRenderedSpinner();
	if (isAgent) {
		return (
			<Box testId={testId} role="status">
				<AgentCardLoading />
			</Box>
		);
	}
	return (
		<CardPopupWrapper>
			<Box testId={testId} role="status">
				<Stack space="space.150" xcss={styles.inner}>
					<HeaderLoading profileData={profileData} isReduced={isReduced} />
					<ManagerSectionLoading profileData={profileData} />
					{!isReduced && <RecentWorkSectionSkeleton />}
				</Stack>
			</Box>
		</CardPopupWrapper>
	);
}
